<template>
  <div
    class="medical-checklist"
    :class="[toneClass, { 'is-invalid': invalid || tooLong }]"
    :data-testid="testid"
  >
    <div class="mc-row">
      <span class="mc-title">
        {{ title }}<span v-if="required" class="text-danger"> *</span>
      </span>
      <div class="mc-seg" role="group" :aria-label="title">
        <button
          type="button"
          :aria-pressed="String(state.has === true)"
          class="mc-has"
          :class="{ on: state.has === true }"
          :data-testid="`${testid}-has`"
          @click="setHas(true)"
        >{{ $t('customer.medical.has') }}</button>
        <button
          type="button"
          :aria-pressed="String(state.has === false)"
          :class="{ on: state.has === false }"
          :data-testid="`${testid}-none`"
          @click="setHas(false)"
        >{{ $t('customer.medical.none') }}</button>
      </div>
      <button
        type="button"
        class="mc-edit"
        :disabled="state.has !== true"
        :aria-expanded="String(open)"
        :data-testid="`${testid}-edit`"
        @click="open = !open"
      >{{ $t('customer.medical.edit') }}</button>
    </div>
    <div v-if="hasChips" class="mc-summary">
      <span v-for="c in chips" :key="c" class="mc-chip">{{ c }}</span>
    </div>
    <!-- มี but nothing picked yet; มี/ไม่มี/unanswered need no caption, the buttons show it -->
    <div v-else-if="state.has === true && !open" class="mc-summary mc-muted">
      {{ $t('customer.medical.pickItems') }}
    </div>

    <div v-if="open" class="mc-panel" @keydown.esc.stop.prevent="open = false">
      <div class="mc-search">
        <i class="fas fa-search" aria-hidden="true"></i>
        <!-- Enter would submit the patient form around us -->
        <input
          ref="search"
          v-model="query"
          type="search"
          class="form-control form-control-sm"
          :placeholder="$t('customer.medical.search')"
          :aria-label="$t('customer.medical.search')"
          :data-testid="`${testid}-search`"
          @keydown.enter.prevent
        />
      </div>
      <div class="mc-list">
        <div v-if="!visibleGroups.length" class="mc-muted py-2">{{ $t('customer.medical.noMatch') }}</div>
        <div v-for="g in visibleGroups" :key="g.label || '_'">
          <div v-if="g.label" class="mc-group">{{ g.label }}</div>
          <div class="mc-grid">
            <div v-for="i in g.items" :key="i.label">
              <label class="mb-0 mc-check">
                <input type="checkbox" :checked="i.label in state.ticked" @change="toggle(i.label)" />
                <span>{{ i.label }}</span>
              </label>
              <input
                v-if="i.detail && i.label in state.ticked"
                class="form-control form-control-sm mt-1"
                :placeholder="i.detail"
                :value="state.ticked[i.label]"
                @input="setDetail(i.label, $event.target.value)"
                @keydown.enter.prevent
              />
            </div>
          </div>
        </div>
      </div>
      <label class="mb-0 d-block mt-2">
        <span class="mc-muted">{{ $t('customer.medical.other') }}</span>
        <input
          class="form-control form-control-sm"
          :value="state.other"
          :data-testid="`${testid}-other`"
          @input="setOther($event.target.value)"
          @keydown.enter.prevent
        />
      </label>
      <div class="d-flex align-items-center mt-2">
        <small v-if="tooLong" class="text-danger">{{ $t('customer.medical.tooLong', { max: maxLen }) }}</small>
        <button type="button" class="btn btn-primary btn-sm ml-auto" @click="open = false">
          {{ $t('customer.medical.done') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// One health row of the patient form (การแพ้ / โรคประจำตัว / ยาที่ใช้ประจำ): a summary, มี/ไม่มี,
// and a panel of tick boxes. v-model on one of the patient text fields; ticks are rebuilt from
// the text and saved back as text (see utils/medicalChecklist).
import { MAX_LEN, decodeChecklist, encodeChecklist, filterGroups } from "@/utils/medicalChecklist";

export default {
  name: "MedicalChecklist",
  props: {
    value: { type: String, default: null },
    title: { type: String, required: true },
    // either a flat item list or [{ label, items }] groups
    items: { type: Array, default: null },
    groups: { type: Array, default: null },
    required: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    // tint the row when something is ticked: "danger" (allergies) or "warning"
    tone: { type: String, default: null },
    testid: { type: String, default: "medical-checklist" },
  },
  data() {
    return {
      state: { has: null, ticked: {}, other: "" },
      lastEmitted: undefined,
      open: false,
      query: "",
      maxLen: MAX_LEN,
    };
  },
  computed: {
    shownGroups() {
      return this.groups || [{ label: null, items: this.items || [] }];
    },
    visibleGroups() {
      return filterGroups(this.shownGroups, this.query);
    },
    flatItems() {
      return this.shownGroups.flatMap((g) => g.items);
    },
    encoded() {
      return encodeChecklist(this.state, this.flatItems);
    },
    tooLong() {
      return (this.encoded || "").length > MAX_LEN;
    },
    chips() {
      const ticked = Object.keys(this.state.ticked).map((l) => [l, this.state.ticked[l]].filter(Boolean).join(" "));
      return this.state.other.trim() ? [...ticked, this.state.other.trim()] : ticked;
    },
    hasChips() {
      return this.state.has === true && this.chips.length > 0;
    },
    toneClass() {
      return this.tone && this.hasChips ? `mc-${this.tone}` : null;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        // our own emit coming back: keep the state, or a half-typed detail would be lost
        if (v === this.lastEmitted) return;
        this.state = decodeChecklist(v, this.flatItems);
        this.open = false;
      },
    },
    open(isOpen) {
      this.query = "";
      if (isOpen) {
        document.addEventListener("mousedown", this.closeOnOutside);
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
      } else {
        document.removeEventListener("mousedown", this.closeOnOutside);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.closeOnOutside);
  },
  methods: {
    closeOnOutside(e) {
      if (!this.$el.contains(e.target)) this.open = false;
    },
    emit() {
      this.lastEmitted = this.encoded;
      this.$emit("input", this.lastEmitted);
    },
    setHas(has) {
      this.state.has = has;
      this.open = has;
      this.emit();
    },
    toggle(label) {
      if (label in this.state.ticked) this.$delete(this.state.ticked, label);
      else this.$set(this.state.ticked, label, "");
      this.emit();
    },
    setDetail(label, text) {
      this.state.ticked[label] = text;
      this.emit();
    },
    setOther(text) {
      this.state.other = text;
      this.emit();
    },
  },
};
</script>

<style lang="scss" scoped>
.medical-checklist {
  position: relative;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  font-size: 0.875rem;

  &.mc-danger {
    background: #fff8f7;
    border-color: #fecdca;

    .mc-title { color: #912018; }
    .mc-chip { background: #fee4e2; color: #912018; }
    .mc-seg button.on.mc-has { background: #d92d20; }
  }
  &.mc-warning {
    background: #fffcf5;
    border-color: #fedf89;

    .mc-title { color: #7a2e0e; }
    .mc-chip { background: #fef0c7; color: #7a2e0e; }
    .mc-seg button.on.mc-has { background: #b54708; }
  }
  &.is-invalid {
    border-color: #dc3545;
  }
}
.mc-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 6px 10px;
}
.mc-title {
  flex: 0 0 auto;
  font-weight: 600;
  color: #101828;
}
.mc-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 10px 10px;
}
.mc-chip {
  padding: 2px 9px;
  border-radius: 999px;
  background: #e3edfb;
  color: #0f3d6e;
  font-size: 0.8125rem;
}
.mc-muted {
  min-width: 0;
  color: #667085;
  font-size: 0.8125rem;
}
.mc-seg {
  display: flex;
  flex: 0 0 auto;
  margin-left: auto;
  border: 1px solid #d0d5dd;
  border-radius: 7px;
  overflow: hidden;

  button {
    min-width: 48px;
    height: 28px;
    border: 0;
    background: #fff;
    color: #344054;

    & + button {
      border-left: 1px solid #d0d5dd;
    }
    &.on {
      background: #1554a0;
      color: #fff;
    }
    &:disabled {
      opacity: 0.6;
    }
  }
}
.mc-edit {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: #1554a0;

  &:disabled {
    color: #98a2b3;
  }
}
.mc-panel {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  min-width: 340px;
  padding: 12px;
  background: #fff;
  border: 1px solid #b9c2cf;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.18);
}
.mc-search {
  position: relative;
  margin-bottom: 8px;

  i {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #98a2b3;
    font-size: 0.75rem;
    pointer-events: none;
  }
  input {
    padding-left: 28px;
  }
}
.mc-list {
  max-height: 300px;
  overflow-y: auto;
}
.mc-group {
  font-weight: 600;
  margin-top: 6px;
}
.mc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: 4px 12px;
}
.mc-check {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  line-height: 1.35;

  input {
    margin-top: 3px;
  }
}
</style>
